import React, { useState } from 'react';
import { STEP_PAY_METHODS, isVisit, markPaid, scheduleStep, stepDate, stepDone, stepKind, stepType, stepUndo, stepWho } from '../../services/jobs';

/**
 * Under an approved plan:
 *   Now          the current step — ✓ Mark done (date, today by default) and 📅 a date: a visit is booked
 *                (date & time + who's coming), a payment / office step gets a due date.
 *   Coming up    every later step with the same buttons, so visits get booked ahead (e.g. while waiting for the
 *                deposit) and any step can be ticked whenever it happens — out of order is fine (Heemo 2026-11-03),
 *                Zoho's note just says "(out of order)".
 * Undo un-ticks the last done step (admin). Deposit / Final payment also tick themselves from the payment sync.
 */
const pad = (n) => String(n).padStart(2, '0');
const todayLocal = () => {
  const d = new Date();
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};
// Zoho's "2026-11-20T09:00:00-05:00" → the datetime-local input's "2026-11-20T09:00"
const toInput = (iso) => (iso ? String(iso).slice(0, 16) : '');
const AUTO_TICK = ['deposit', 'final_payment'];

const StepButtons = ({ job, steps, isAdmin, onToast }) => {
  const cur = steps.find((s) => s.status === 'current');
  const later = steps.filter((s) => s.status === 'pending');
  const anyDone = steps.some((s) => s.status === 'done');
  const [open, setOpen] = useState(null); // { key, mode: 'done' | 'book' }
  const [date, setDate] = useState(todayLocal());
  const [at, setAt] = useState('');
  const [who, setWho] = useState('');
  const [due, setDue] = useState('');
  const [pay, setPay] = useState({ invoice: '', method: '', amount: '', notes: '' });
  const [busy, setBusy] = useState(false);
  const owing = Number(job.balance || 0);

  const start = (step, mode) => {
    setDate(todayLocal());
    setAt(toInput(step.when));
    setWho(stepWho(step, job));
    setDue(step.due || '');
    setPay({ invoice: job.invoiceNumber || '', method: '', amount: step.key === 'final_payment' && owing > 0 ? owing.toFixed(2) : '', notes: '' });
    setOpen({ key: step.key, mode });
  };
  const run = async (fn) => {
    setBusy(true);
    try {
      onToast(await fn());
      setOpen(null);
    } catch (err) {
      onToast(`⚠ ${err.message}`);
    } finally {
      setBusy(false);
    }
  };
  const has = (step) => (isVisit(step) ? !!step.when : !!step.due);

  const form = (step) => {
    if (!open || open.key !== step.key) return null;
    const outOfOrder = !!cur && step.key !== cur.key;
    const cancel = <button type="button" className="status-cancel" onClick={() => setOpen(null)} disabled={busy}>Cancel</button>;
    if (open.mode === 'done' && stepKind(step) === 'payment') {
      // how it was paid → ticked with the details; anything but card is also recorded in Books
      const toBooks = pay.method && pay.method !== 'card' && !!job.invoiceNumber;
      const set = (patch) => setPay({ ...pay, ...patch });
      return (
        <div className="step-form">
          <label>
            Invoice #
            <input value={pay.invoice} onChange={(e) => set({ invoice: e.target.value })} placeholder={job.invoiceNumber || 'No invoice sent yet'} maxLength={30} />
          </label>
          <label>
            Payment method
            <select value={pay.method} onChange={(e) => set({ method: e.target.value })}>
              <option value="">Pick one…</option>
              {STEP_PAY_METHODS.map(([id, label]) => <option key={id} value={id}>{label}</option>)}
            </select>
          </label>
          <label>
            Amount {!toBooks && <span className="status-optional">optional</span>}
            <input type="number" inputMode="decimal" min="0" step="0.01" value={pay.amount} onChange={(e) => set({ amount: e.target.value })} placeholder={owing ? `Up to ${owing.toFixed(2)}` : '0.00'} />
          </label>
          {owing > 0 && (
            <button type="button" className="plan-link pay-full" onClick={() => set({ amount: owing.toFixed(2) })}>Full balance ${owing.toFixed(2)}</button>
          )}
          <label>
            Paid on
            <input type="date" value={date} max={todayLocal()} onChange={(e) => setDate(e.target.value)} />
          </label>
          <label>
            Notes <span className="status-optional">optional</span>
            <textarea className="status-textarea" rows={2} value={pay.notes} onChange={(e) => set({ notes: e.target.value })} placeholder="e-Transfer ref, cheque #, anything else" maxLength={1000} />
          </label>
          <p className="ca-muted">
            {!pay.method ? 'Pick how they paid.' : pay.method === 'card' ? 'Card payments are already in Books — this just ticks the step.' : toBooks ? `Also records the payment in Books on ${job.invoiceNumber}.` : 'No invoice on this job yet — record it in Books once it’s sent.'}
          </p>
          <div className="plan-actions">
            {cancel}
            <button type="button" className="status-save" onClick={() => run(() => markPaid(job, step, { ...pay, date }, outOfOrder))} disabled={busy || !date || !pay.method || (toBooks && !(Number(pay.amount) > 0))}>
              {busy ? 'Saving…' : `✓ ${step.label} done`}
            </button>
          </div>
        </div>
      );
    }
    if (open.mode === 'done') {
      return (
        <div className="step-form">
          <label>
            Done on
            <input type="date" value={date} max={todayLocal()} onChange={(e) => setDate(e.target.value)} />
          </label>
          <div className="plan-actions">
            {cancel}
            <button type="button" className="status-save" onClick={() => run(() => stepDone(job.id, step.key, date, outOfOrder))} disabled={busy || !date}>
              {busy ? 'Saving…' : `✓ ${step.label} done`}
            </button>
          </div>
        </div>
      );
    }
    if (!isVisit(step)) {
      return (
        <div className="step-form">
          <label>
            Due by
            <input type="date" value={due} onChange={(e) => setDue(e.target.value)} />
          </label>
          <div className="plan-actions">
            {cancel}
            <button type="button" className="status-save" onClick={() => run(() => scheduleStep(job.id, step, { due }))} disabled={busy || !due}>
              {busy ? 'Saving…' : 'Save due date'}
            </button>
          </div>
        </div>
      );
    }
    return (
      <div className="step-form">
        <label>
          Date & time
          <input type="datetime-local" value={at} onChange={(e) => setAt(e.target.value)} />
        </label>
        <label>
          {stepType(step).who || 'Who’s coming'}
          <input value={who} onChange={(e) => setWho(e.target.value)} placeholder="Name of the person or crew" maxLength={120} />
        </label>
        <div className="plan-actions">
          {cancel}
          <button type="button" className="status-save" onClick={() => run(() => scheduleStep(job.id, step, { at, who }))} disabled={busy || !at}>
            {busy ? 'Saving…' : 'Save booking'}
          </button>
        </div>
      </div>
    );
  };
  const buttons = (step, big) => {
    const visit = isVisit(step);
    const label = big
      ? visit ? (has(step) ? 'Change booking' : 'Book date & time') : has(step) ? 'Change due date' : 'Set due date'
      : has(step) ? 'Change' : visit ? 'Book' : 'Due date';
    return (
      <div className={big ? 'plan-actions' : 'step-row-tools'}>
        <button type="button" className="status-cancel" onClick={() => start(step, 'book')} disabled={busy}>📅 {label}</button>
        <button type="button" className={big ? 'status-save' : 'status-cancel'} onClick={() => start(step, 'done')} disabled={busy}>
          ✓ {big ? 'Mark done' : 'Done'}
        </button>
      </div>
    );
  };
  const dateLine = (step) => {
    const d = stepDate(step, job);
    return <em className={d.tone}>{d.text}</em>;
  };

  return (
    <div className="step-panel">
      {cur ? (
        <>
          <div className="step-now">
            <span className="step-now-label">Now</span>
            <b>{cur.label}</b>
            <span className="step-now-when">{dateLine(cur)}</span>
            {AUTO_TICK.includes(cur.key) && <span className="step-now-when">Ticks itself when the payment comes in</span>}
          </div>
          {open && open.key === cur.key ? form(cur) : buttons(cur, true)}
        </>
      ) : (
        <p className="ca-muted">Every step is done.</p>
      )}

      {later.length > 0 && (
        <div className="step-later">
          <div className="step-now-label">Coming up</div>
          {later.map((s) => (
            <div key={s.key} className="step-row">
              <div className="step-row-head">
                <span>{s.label}</span>
                {dateLine(s)}
                {(!open || open.key !== s.key) && buttons(s, false)}
              </div>
              {form(s)}
            </div>
          ))}
        </div>
      )}

      {isAdmin && anyDone && !open && (
        <button type="button" className="plan-link step-undo" onClick={() => run(() => stepUndo(job.id))} disabled={busy}>
          ↺ Undo last tick
        </button>
      )}
    </div>
  );
};

export default StepButtons;
